import React from "react";
import { Helmet } from "react-helmet";
import AgentDirectorySearch, { useDirectorySearchActions } from "@/components/AgentDirectorySearch";
import DirectoryMarketCards, { marketsFromCities } from "@/components/DirectoryMarketCards";
import { statePath } from "@/components/DirectoryBreadcrumb";
import { useDirectoryStates } from "@/hooks/useAgentDirectory";
import useAvailableCities from "@/hooks/useAvailableCities";
import "./DirectoryHome.scss";
import AppLink from "@/components/AppLink";
import DirectoryHomeAgents from "@/components/DirectoryHomeAgents";

export const StateLinks = ({ query }) => {
  const states = query.data?.states || [];
  if (query.isLoading) {
    return (
      <ul className="directory-states" aria-hidden="true">
        {Array.from({ length: 4 }, (_, index) => (
          <li key={index}>
            <span className="directory-states__link is-loading" />
          </li>
        ))}
      </ul>
    );
  }
  if (query.isError) {
    return (
      <div className="directory-states__error">
        <p>The list of states did not load.</p>
        <button type="button" onClick={() => query.refetch()}>
          Try again
        </button>
      </div>
    );
  }
  return states.length ? (
    <ul className="directory-states">
      {states.map((state) => (
        <li key={state.slug}>
          <AppLink className="directory-states__link" href={statePath(state.slug)}>
            {state.name}
          </AppLink>
        </li>
      ))}
    </ul>
  ) : null;
};

const DirectoryHome = ({ location }) => {
  const { featuredCities } = useAvailableCities();
  const actions = useDirectorySearchActions();
  const statesQuery = useDirectoryStates();
  const markets = marketsFromCities(featuredCities);
  const noStates = !statesQuery.isLoading && !statesQuery.isError && !(statesQuery.data?.states || []).length;

  return (
    <>
      <Helmet>
        <title>Real estate agent directory | Gitcha</title>
        <meta
          name="description"
          content="Browse real estate agents by state and city on Gitcha. Agents working with buyers right now are listed first."
        />
      </Helmet>
      <header className="directory-page__head">
        <h1>Agent directory</h1>
        <p>
          Real estate agents by state and city, with the buyer demand each one is working with. Agents working with
          buyers right now come&nbsp;first.
        </p>
      </header>

      <AgentDirectorySearch location={location} {...actions} />

      {markets.length ? (
        <section className="directory-section" aria-labelledby="directory-featured">
          <h2 id="directory-featured">Featured markets</h2>
          <DirectoryMarketCards markets={markets} />
        </section>
      ) : null}

      <DirectoryHomeAgents saved={location} />

      {noStates ? null : (
        <section className="directory-section" aria-labelledby="directory-states">
          <h2 id="directory-states">Browse by state</h2>
          <StateLinks query={statesQuery} />
        </section>
      )}
    </>
  );
};

export default DirectoryHome;
